import type { BcModule } from '../../services/components-helpers/components.service';
import { BasicModel } from '../basic/basic';
import { ResponsiveAttribute } from '../basic/responsive-attribute';
import { IFontConfigStyle, IFontConfigWeight } from '../build-template/template-config';
import { GsStructComponent, PageComponentType } from '../structure';
import { IColorTypes } from './colors/colors';
import { IConfigFooter } from './footer/footer';
import { IConfigHeader } from './header/header';
import { IUiElements } from './ui-elements';

export interface ConfigModel extends BasicModel {
  /** The Timestamp when the config was updated the last time */
  updatedAt?: string;
  /** The version of the config (integer without decimals) */
  version: number;
  /** The key to identify the app which was/is using this config (only for localstorage) */
  appKey?: string;
  /** The interval in minutes in which the config should be polled from the backend (default: 5) */
  pollingInterval?: number;
  /**
   * The interval in minutes in which the shop init data (menus, payment/shipping methods,
   * currencies, company info, logo, consent) should be polled from the backend (default: 60)
   */
  initPollingInterval?: number;
  /** Settings for the complete header incl. topbar, search and menubar */
  header?: IConfigHeader;
  /** Settings for the complete footer */
  footer?: IConfigFooter;
  /** All colors to use inside tailwind */
  colors?: IColorTypes;
  /** Predefined styles for different tags like h1, h2 etc. */
  tagStyles?: Record<ConfigStyleTag, ResponsiveAttribute<IConfigTagStyle>>;
  /** All default setting for all components */
  components?: Record<PageComponentType, GsStructComponent<any>>;
  /** Reusable UI element settings (buttons, etc.) */
  uiElements?: IUiElements;
  /** SEO settings that apply to every page (e.g. search-engine site verification) */
  seo?: IConfigSeo;
  /** State of the module setups run by the designer's setup wizard */
  initialSetup?: IConfigInitialSetup;
}

/** State of the module setups run by the designer's setup wizard. */
export interface IConfigInitialSetup {
  /** The setup state per booked module */
  modules: Partial<Record<BcModule, IModuleInfo>>;
}

/** Setup state of a single module. */
export interface IModuleInfo {
  /**
   * The last run of the setup for this module, keyed by channel ID. The config lives in the template,
   * which several channels can share, while the pages the setup creates belong to a single channel.
   */
  lastRuns: Record<string, IModuleSetupRun>;
}

/** A single run of a module setup. */
export interface IModuleSetupRun {
  /** ISO timestamp the setup was started */
  startedOn: string;
  /** ISO timestamp the setup was finished */
  finishedOn: string;
  /** Whether every task of the setup succeeded */
  successful: boolean;
  /** Version of the setup that ran */
  version: number;
}

/** Platform-wide SEO settings maintained in the config wizard. */
export interface IConfigSeo {
  /**
   * Content of the `google-site-verification` meta tag from the Google Search Console
   * "HTML-Tag" verification method. Must stay in place permanently — removing it revokes
   * the ownership verification.
   */
  googleSiteVerification?: string;
}

/**
 * A `ConfigModel` holding every section the shipped defaults provide (see `DEFAULT_CONFIG`). Customer
 * configs stay partial, so only the defaults get the non-optional sections.
 */
export type DefaultConfigModel = ConfigModel & Required<Pick<ConfigModel, 'header' | 'footer' | 'colors' | 'uiElements' | 'tagStyles'>>;

/** Interface for spacing on all four sides */
export interface ISides<T = number> {
  /** The left spacing (old: enum (tailwind-class) // new: pixel value) */
  left: T;
  /** The top spacing (old: enum (tailwind-class) // new: pixel value) */
  top: T;
  /** The right spacing (old: enum (tailwind-class) // new: pixel value) */
  right: T;
  /** The bottom spacing (old: enum (tailwind-class) // new: pixel value) */
  bottom: T;
}

/** Styleable html-tags for the config and the config-wizard */
export type ConfigStyleTag = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p';
/** Styleable html-tags for the config and the config-wizard */
export const CONFIG_FONT_TAGS: ConfigStyleTag[] = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p'];

/** Possible useable font-weight-classes from tailwind */
export const CONFIG_FONT_WEIGHT_MAP: Record<IFontConfigWeight, string> = {
  100: 'thin',
  200: 'extralight',
  300: 'light',
  400: 'normal',
  500: 'medium',
  600: 'semibold',
  700: 'bold',
  800: 'extrabold',
  900: 'black'
};

/** Text transform types for config */
export type TextTransformType = 'none' | 'uppercase' | 'lowercase' | 'capitalize';

/** Possible text-transform classes from tailwind */
export const CONFIG_TEXT_TRANSFORM_MAP: Record<TextTransformType, string> = {
  none: 'normal-case',
  uppercase: 'uppercase',
  lowercase: 'lowercase',
  capitalize: 'capitalize'
};

/** Default styling for a html-tag in the app */
export interface IConfigTagStyle {
  /** Font-size in rem */
  size: number;
  /** Line-height in rem */
  lineHeight?: number;
  /** The font-weight in numbers 100-900 */
  weight: IFontConfigWeight;
  /** The font name used (can be from template) */
  family: string;
  /** Style of the text (e.g. italic or normal) */
  style: IFontConfigStyle;
  /** Color of the text */
  color?: string;
  /** Text transform (uppercase, lowercase, capitalize, none) */
  textTransform: TextTransformType;
  /** Top spacing above headings in px. Empty → falls back to the EditorJS default (0.6em) */
  spacingTop?: number;
  /** Bottom spacing below headings in px. Empty → falls back to the EditorJS default (3px) */
  spacingBottom?: number;
}

export type ConfigTarget = '_blank' | '_self';

/**
 * The possible alignments for different components
 * @deprecated use the GsStructHorizontalAlignment enum instead
 */
export enum CONFIG_ALIGNMENT {
  LEFT = 0,
  CENTER = 1,
  RIGHT = 2
}

/** The possible types of newsletter templates */
export enum CONFIG_FOOTER_NEWSLETTER_TYPE {
  DATASECURITY_CHECK = 0,
  ONLY_BUTTON = 1
}
